import { AppRoutingModule } from './app/app.routing.module';

import { AppComponent } from './app/app.component';

import { AuthService } from './app/auth/auth.service';
import { EppService } from './app/epp/epp.service';
import { PigeonsService } from './app/pigeons/pigeons.service';
import { RadarService } from './app/radar/radar.service';
import { PaginatorIntlService } from './app/shared/paginator-intl.service';
import { StationService } from './app/shared/station.service';
import { SmwService } from './app/smw/smw.service';
import { WarningService } from './app/warning/warning.service';
import { CookieService } from 'ngx-cookie-service';

import { HttpInterceptorProviders } from './app/interceptors/interceptor-provider';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import {
  FullscreenOverlayContainer,
  OverlayContainer,
} from '@angular/cdk/overlay';
import { PlatformModule } from '@angular/cdk/platform';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideExperimentalZonelessChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatPaginatorIntl,
  MatPaginatorModule,
} from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NgxSliderModule } from '@angular-slider/ngx-slider';

import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';

import { provideMapboxGL } from 'ngx-mapbox-gl';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import awsconfig from './aws-exports';
import { Amplify } from '@aws-amplify/core';

import { environment } from './environments/environment';

import 'moment/locale/es';
import 'moment/locale/ca';
import 'moment/locale/de';
import 'moment/locale/nl';
import 'moment/locale/fr';
import 'moment/locale/en-gb';
import { RaygunErrorHandler } from './app/app.raygun.setup';
import { CustomTranslocoHttpLoader } from './transloco/loader';
import { CsvModule } from '@ctrl/ngx-csv';
import { provideTransloco } from '@jsverse/transloco';
import { TippyDirective } from '@ngneat/helipopper';
import {
  popperVariation,
  provideTippyConfig,
  provideTippyLoader,
  tooltipVariation,
} from '@ngneat/helipopper/config';
import { provideTranslocoInitializer } from '@wettermanufaktur-gmbh/ui-sdk/initializers/transloco';
import { ValidationErrorsConfig } from '@wettermanufaktur-gmbh/ui-sdk/models/validation';
import { provideValidationErrorsConfig } from '@wettermanufaktur-gmbh/ui-sdk/providers/form-errors';
import { HighchartsChartModule } from 'highcharts-angular';
import moment from 'moment';
import { appInitializerFactory, HttpLoaderFactory } from 'src/constants';

/* Configure Amplify resources */
Amplify.configure(awsconfig);

if (environment.production) {
  enableProdMode();
  if (window && !environment.beta) {
    window.console.log = () => {};
    console.warn('Console OFF');
  }
}

const validationErrorsConfig: ValidationErrorsConfig = {
  required: () => {
    return {
      translate: true,
      message: 'shared.validation.required',
    };
  },
  minlength: (error: { [key: string]: unknown }) => {
    return {
      translate: true,
      message: 'shared.validation.min_length',
      params: {
        requiredLength: error['requiredLength'],
        actualLength: error['actualLength'],
      },
    };
  },
  maxlength: (error: { [key: string]: unknown }) => {
    return {
      translate: true,
      message: 'shared.validation.max_length',
      params: {
        requiredLength: error['requiredLength'],
        actualLength: error['actualLength'],
      },
    };
  },
  matDatepickerFilter: () => {
    return {
      translate: true,
      message: 'shared.validation.date_filter',
    };
  },
  matDatepickerParse: () => {
    return {
      message: 'shared.validation.mat_datepicker_parse',
      translate: true,
    };
  },
  matDatepickerMin: (err: { [key: string]: unknown }) => {
    return {
      message: 'shared.validation.mat_datepicker_min',
      translate: true,
      params: { minDate: moment(err['min'] as Date).format('MM-DD-YYYY') },
    };
  },
  matDatepickerMax: (err: { [key: string]: unknown }) => ({
    message: 'shared.validation.mat_datepicker_max',
    translate: true,
    params: { maxDate: moment(err['max'] as Date).format('MM-DD-YYYY') },
  }),
  email: () => {
    return {
      translate: true,
      message: 'shared.validation.email',
    };
  },
  hasSpecialCharacters: () => {
    return {
      translate: true,
      message: 'shared.validation.has_special_characters',
    };
  },
  selectedElementsRequired: () => {
    return {
      translate: true,
      message: 'shared.validation.table_element_required',
    };
  },
  autocompleteRequired: () => {
    return {
      translate: true,
      message: 'shared.validation.autocomplete_required',
    };
  },
  dateValidator: () => {
    return {
      message: 'shared.validation.date_validation',
      translate: true,
    };
  },
  endDateValidator: () => {
    return {
      message: 'shared.validation.end_date_validation',
      translate: true,
    };
  },
  usernameExists: () => {
    return {
      message: 'PRODUCT.USERADMIN.STAMMDATEN.EMAILALREADYEXIST',
      translate: true,
    };
  },
};

bootstrapApplication(AppComponent, {
  providers: [
    provideExperimentalZonelessChangeDetection(),
    importProvidersFrom(
      // ANGULAR MODULES
      AppRoutingModule,
      BrowserModule,
      PlatformModule,
      ReactiveFormsModule,
      FormsModule,
      // ANGULAR MATERIAL MODULES
      MatToolbarModule,
      MatButtonModule,
      MatSidenavModule,
      MatIconModule,
      MatListModule,
      MatGridListModule,
      MatCardModule,
      MatMenuModule,
      MatFormFieldModule,
      MatInputModule,
      MatExpansionModule,
      MatAutocompleteModule,
      MatSelectModule,
      MatTableModule,
      MatTabsModule,
      MatTooltipModule,
      MatPaginatorModule,
      MatDatepickerModule,
      MatMomentDateModule,
      MatFormFieldModule,
      MatDialogModule,
      MatCheckboxModule,
      MatSnackBarModule,
      MatStepperModule,
      MatChipsModule,
      MatSlideToggleModule,
      MatProgressBarModule,
      MatBadgeModule,
      MatProgressSpinnerModule,
      MatCheckboxModule,
      LayoutModule,
      DragDropModule,
      MatButtonToggleModule,
      // THIRD PARTY MODULES
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      FontAwesomeModule,
      HighchartsChartModule,
      CsvModule,
      NgxSliderModule,
      // STANDALONE ELEMENTS
      TippyDirective
    ),
    // ANGULAR MATERIAL PROVIDERS
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    {
      provide: OverlayContainer,
      useClass: FullscreenOverlayContainer,
    },
    {
      provide: MatPaginatorIntl,
      useFactory: translate => {
        const service = new PaginatorIntlService();
        service.injectTranslateService(translate);
        return service;
      },
      deps: [TranslateService],
    },
    // THIRD PARTY PROVIDERS
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(inject(TranslateService));
      return initializerFn();
    }),
    environment.production
      ? {
          provide: ErrorHandler,
          useClass: RaygunErrorHandler,
        }
      : [],
    CookieService,
    provideMapboxGL({
      accessToken:
        'pk.eyJ1IjoiY29ubnlicnVobiIsImEiOiJjanBqaHU2aWMwNmY0M3FsdjVyaXo0N21lIn0.wOrequAwl4sj8xvEzP5_6A',
    }),
    provideTippyLoader(() => import('tippy.js')),
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation,
      },
    }),
    // APP PROVIDERS
    AuthService,
    SmwService,
    RadarService,
    EppService,
    WarningService,
    PigeonsService,
    StationService,
    HttpInterceptorProviders,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideTransloco({
      config: {
        availableLangs: ['us', 'de', 'es', 'fr', 'nl'],
        defaultLang: 'us',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: { allowEmpty: true, logMissingKey: true },
      },
      loader: CustomTranslocoHttpLoader,
    }),
    provideTranslocoInitializer(),
    provideValidationErrorsConfig(validationErrorsConfig),
  ],
}).catch(err => console.error(err));
